function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import Icon from '../../icon';
import { fontSemiBold } from '../../theme/fonts';
import { white, primary100, primary600, gray400, gray700, gray800, focusShadow } from '../../theme/colors';
import { size4, size8, size14, size18, size24 } from '../../theme/scale';
import { duration150, easeInOut } from '../../theme/animation';
import { labelFontSize } from "../../utils/helpers";
const checkBoxSize = size18;
const checkboxLineHeight = '1.5';
const Box = styled.div`
  border: 2px solid ${gray700};
  border-radius: ${size4};
  display: block;
  height: ${checkBoxSize};
  position: relative;
  transition: border-color ${duration150} ${easeInOut},
    box-shadow ${duration150} ${easeInOut},
    background ${duration150} ${easeInOut};
  width: ${checkBoxSize};
`;
const CheckIcon = styled(Icon)`
  fill: ${white};
  height: 0;
  left: 50%;
  opacity: 0;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: height ${duration150} ${easeInOut},
    width ${duration150} ${easeInOut}, opacity ${duration150} ${easeInOut};
  width: 0;
`;
const Wrapper = styled.span`
  display: block;
  flex: none;
  ${({
  hasLabel
}) => hasLabel && `margin-right: ${size14};`}
  position: relative;
  top: calc( (${labelFontSize} * ${checkboxLineHeight} - ${checkBoxSize}) / 2);
`;
const Input = styled.input`
  cursor: inherit;
  height: 100%;
  left: 0;
  margin: 0;
  opacity: 0;
  padding: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1;

  &:hover {
    ~ ${Box} {
      border-color: ${primary600};
    }
  }

  &:checked {
    ~ ${Box} {
      background: ${primary600};
      border-color: ${primary600};

      > ${CheckIcon} {
        height: ${size14};
        opacity: 1;
        width: ${size14};
      }
    }

    &:disabled {
      ~ ${Box} {
        background: ${primary100};
        border-color: ${primary100};
      }
    }
  }

  &:disabled {
    ~ ${Box} {
      border-color: ${gray400};
    }
  }

  &:focus {
    + ${Box} {
      border-color: ${primary600};
      box-shadow: 0 0 0 3px ${focusShadow};
    }
  }
`;
const Label = styled.label`
  align-items: flex-start;
  position: relative;
  line-height: ${checkboxLineHeight};
  color: ${gray800};
  cursor: pointer;
  display: inline-flex;
  font-family: ${fontSemiBold};
  font-size: ${labelFontSize};
  ${({
  hideLabel
}) => hideLabel && `height: ${size24};`}

  &:not(:last-of-type) {
    ${({
  horizontal
}) => horizontal ? `margin-right: ${size14};` : `margin-bottom: ${size8}`}
  }
`;
export const Checkbox = ({
  label,
  checked,
  hideLabel = false,
  disabled = false,
  onChange,
  horizontal = false,
  smallLabel = false,
  value,
  id,
  ...props
}, ref) => {
  const toggleChecked = () => {
    if (!onChange || disabled) return;
    onChange(!checked);
  };
  const hasLabel = typeof label === 'string' && !!label?.trim();
  const ariaAttributes = hideLabel && {
    'aria-label': label
  };
  return /*#__PURE__*/React.createElement(Label, {
    smallLabel,
    horizontal: horizontal,
    hideLabel: hideLabel
  }, /*#__PURE__*/React.createElement(Wrapper, {
    smallLabel,
    hasLabel
  }, /*#__PURE__*/React.createElement(Input, _extends({
    type: "checkbox",
    checked,
    disabled,
    id,
    value
  }, ariaAttributes, {
    onChange: toggleChecked,
    ref: ref
  }, props)), /*#__PURE__*/React.createElement(Box, {
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement(CheckIcon, {
    type: "Check",
    size: 14
  }))), !hideLabel && label);
};
Checkbox.displayName = 'Checkbox';
const ForwardRefCheckbox = /*#__PURE__*/forwardRef(Checkbox);
const CheckboxWrapper = ({
  ref,
  ...rest
}) => /*#__PURE__*/React.createElement(ForwardRefCheckbox, _extends({}, rest, {
  ref: ref
}));
export default CheckboxWrapper;
//# sourceMappingURL=index.js.map